import { Tag } from '@components';
import {
  Alert,
  Box,
  Divider,
  FormControlLabel,
  FormHelperText,
  Switch,
  Typography,
  useTheme,
} from '@mui/material';
import { useQueryClient } from '@tanstack/react-query';
import { Formik, type FormikProps } from 'formik';
import { type FC, useCallback, useContext, useMemo } from 'react';

import { SlabContext } from '@core/context/SlabContext';
import {
  BULK_EDIT_FIELD_MAP,
  BULK_EDIT_PERMISSIONS,
  FormikUser,
  PLANNING_FIELD_MAP,
  PLANNING_PERMISSIONS,
  type UserFormikType,
  UserSchema,
  bulkEditPermissionData,
  getEditableUserPermissions,
  getRoleGrantedPlanningPermissions,
  planningPermissionData,
  setPlanningPermission,
} from '@core/formik/user';
import Enums from '@core/types/api/Enums';
import type { Role } from '@core/types/api/Role/Role';
import { NewUser, User } from '@core/types/api/User/User';
import { hasStringValue, hasValue } from '@core/utils/equality';
import { FormErrorNotification, SetFormikValue, SetFormikValues } from '@core/utils/formikHelpers';
import { Input } from '@web/components/Input/Input';
import {
  OptionsToReferences,
  ReferenceValueMatcher,
} from '@web/components/InputMultiLookup/InputMultiLookup';
import { ApiMultiLookupInput } from '@web/components/LookupInput/ApiMultiLookupInput';
import type { LookupInputOption } from '@web/components/LookupInput/LookupInputSharedComponents';
import { SlabButton } from '@web/components/SlabButton/SlabButton';
import { DrawerCloseHandler } from '@web/components/SlabDrawer/DrawerCloseHandler';
import { SlabDrawer } from '@web/components/SlabDrawer/SlabDrawer';
import { SlabDrawerHeader } from '@web/components/SlabDrawer/SlabDrawerHeader';
import { SlabDrawerLayout } from '@web/components/SlabDrawer/SlabDrawerLayout';
import { useDrawerCloseGuard } from '@web/components/SlabDrawer/useDrawerCloseGuard';
import { useSlabMutation } from '@web/hooks/useSlabMutation';
import { ServerErrorMessage, useSlabQuery } from '@web/hooks/useSlabQuery';
import { useToast } from '@web/providers/ToastProvider/useToast';
import type { ListURLParams } from '@web/utils/ApiClient';
import { lookups } from '@web/utils/DomainHelpers';
import type { QueryError } from '@web/utils/Query';

type Props = {
  isOpen: boolean;
  onDismiss(): void;
  user: User | null;
  tenantIdOverride?: string;
};

export const UserDrawer: FC<Props> = ({ isOpen, onDismiss, user, tenantIdOverride }) => {
  const ctx = useContext(SlabContext);
  const queryClient = useQueryClient();
  const editAsAdmin = hasValue(tenantIdOverride);

  const createUser = useSlabMutation(editAsAdmin ? 'POST user as admin' : 'POST user');
  const updateUser = useSlabMutation(editAsAdmin ? 'PUT user by ID as admin' : 'PUT user by ID');

  const isEditMode = hasValue(user);
  const theme = useTheme();

  const toast = useToast({ subjectName: 'user' });
  const { guardClose, editTracker } = useDrawerCloseGuard();

  const handleSuccess = useCallback(() => {
    toast.success();
    onDismiss();
  }, [toast, onDismiss]);

  const handleError = useCallback(
    (error: QueryError) => {
      toast.error({ message: ServerErrorMessage(error) });
    },
    [toast],
  );

  const isAdmin = ctx.userInfo.hasRoles([Enums.RoleName.SlabstackAdministrator]);
  const { editableRoles, uneditableRoles } = useMemo(() => {
    const roles = user?.roles ?? [];
    return roles.reduce<{ editableRoles: Role[]; uneditableRoles: Role[] }>(
      (state, role) => {
        if (!isAdmin && role.name === Enums.RoleName.SlabstackAdministrator) {
          return {
            editableRoles: state.editableRoles,
            uneditableRoles: [...state.uneditableRoles, role],
          };
        }
        return {
          editableRoles: [...state.editableRoles, role],
          uneditableRoles: state.uneditableRoles,
        };
      },
      { editableRoles: [], uneditableRoles: [] },
    );
  }, [isAdmin, user?.roles]);

  // The roles field only holds references, so the tenant's roles are loaded
  // to know which Planning permissions the pending selection grants.
  const tenantRoles = useSlabQuery('GET roles', {}, { enabled: !editAsAdmin });
  const tenantRolesAsAdmin = useSlabQuery(
    'GET roles as admin',
    { pathParams: { tenantID: tenantIdOverride ?? '' } },
    { enabled: editAsAdmin },
  );
  const adminTenants = useSlabQuery('GET tenants as admin', {}, { enabled: editAsAdmin });
  const restrictsActivityEditing = editAsAdmin
    ? (adminTenants.data?.items.find((tenant) => tenant.id === tenantIdOverride)
        ?.restrictActivityEditingToAssignees ?? false)
    : ctx.userInfo.tenant.restrictActivityEditingToAssignees;
  const rolesById = useMemo(() => {
    const loaded = (editAsAdmin ? tenantRolesAsAdmin.data : tenantRoles.data)?.items ?? [];
    return new Map([...(user?.roles ?? []), ...loaded].map((role) => [role.id, role]));
  }, [editAsAdmin, tenantRoles.data, tenantRolesAsAdmin.data, user?.roles]);

  const resolveRoles = (selected: UserFormikType['roles']): Role[] => [
    ...uneditableRoles,
    ...selected.map(({ id }) => (id === null ? undefined : rolesById.get(id))).filter(hasValue),
  ];

  const roleGrantedPlanning = (selected: UserFormikType['roles']): Set<Enums.Permission> =>
    getRoleGrantedPlanningPermissions(resolveRoles(selected));

  const roleGrantsEditAllActivities = (selected: UserFormikType['roles']): boolean =>
    resolveRoles(selected).some((role) =>
      role.permissions.includes(Enums.Permission.CanEditAllActivities),
    );

  const formikUser = FormikUser(
    user === null
      ? null
      : NewUser({
          ...user,
          roles: editableRoles,
        }),
  );

  const rolesLookupParams: Partial<ListURLParams<Role>> | undefined = isAdmin
    ? undefined
    : {
        filterBy: [
          {
            operation: Enums.FilterOperation.NotEquals,
            name: 'name',
            values: [Enums.RoleName.SlabstackAdministrator],
          },
        ],
      };

  const requestClose = (formikBag: FormikProps<UserFormikType>): void =>
    guardClose({
      isDirty: editTracker.isEdited(),
      canSave: formikBag.isValid,
      isSaving: createUser.isPending || updateUser.isPending,
      onSave: formikBag.submitForm,
      onDiscard: onDismiss,
    });

  return (
    <SlabDrawer isOpen={isOpen} close={onDismiss}>
      <Formik
        validationSchema={UserSchema}
        initialValues={formikUser}
        onSubmit={async (values: UserFormikType, { setSubmitting }): Promise<void> => {
          const isMyUser = ctx.userInfo.user.id === formikUser.id;
          const {
            canBulkEdit,
            canBulkEditQuotes,
            canBulkEditProjects,
            canBulkEditCompanies,
            canBulkEditLocations,
            canBulkEditActivities,
            canViewSensitiveFiles,
            canEditAllActivities,
            canViewPlanning,
            canEditPlanning,
            canApprovePlanning,
            ...userValues
          } = values;
          const wireUser = new User({
            ...userValues,
            userPermissions: getEditableUserPermissions({
              ...userValues,
              canBulkEdit,
              canBulkEditQuotes,
              canBulkEditProjects,
              canBulkEditCompanies,
              canBulkEditLocations,
              canBulkEditActivities,
              canViewSensitiveFiles,
              canEditAllActivities,
              canViewPlanning,
              canEditPlanning,
              canApprovePlanning,
            }),
          });
          wireUser.roles.push(...uneditableRoles);

          const mutationOptions = {
            onSuccess: (): void => {
              setSubmitting(false);
              handleSuccess();
              // if the user is being updated and it's their own user, invalidate all queries
              if (isEditMode && isMyUser) {
                queryClient.invalidateQueries();
              }
            },
            onError: (error: QueryError): void => {
              setSubmitting(false);
              handleError(error);
            },
          };

          if (!isEditMode) {
            createUser.mutate(
              {
                args: { body: wireUser, pathParams: { tenantID: tenantIdOverride ?? '' } },
              },
              mutationOptions,
            );
          } else {
            updateUser.mutate(
              {
                args: {
                  pathParams: { userID: formikUser.id, tenantID: tenantIdOverride },
                  body: wireUser,
                },
              },
              mutationOptions,
            );
          }
        }}
      >
        {(formikBag) => (
          <SlabDrawerLayout>
            <DrawerCloseHandler onClose={() => requestClose(formikBag)} editTracker={editTracker} />
            <Box display='flex' flexDirection='column' gap={3}>
              <SlabDrawerHeader
                title={isEditMode ? 'Edit user' : 'Add new user'}
                onClose={() => requestClose(formikBag)}
                sx={{ mt: 2 }}
              />

              <FormErrorNotification />

              <Box display='flex' flexDirection='column'>
                <Box sx={{ display: 'flex', gap: 2 }}>
                  <Input name='firstName' label='First name*' />
                  <Input name='lastName' label='Last name' />
                </Box>

                <Box sx={{ display: 'flex', gap: 2 }}>
                  <Input name='emailAddress' label='Email address*' type='email' />
                  <Input name='phoneNumber' label='Phone number' type='tel' />
                </Box>

                <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                  <ApiMultiLookupInput
                    formState={formikBag.values}
                    label='Roles'
                    name='roles'
                    route={{
                      barrel: editAsAdmin ? 'GET roles as admin' : 'GET roles',
                      args: (input, { constructQueryParams }) => ({
                        ...constructQueryParams(input, rolesLookupParams),
                        pathParams: {
                          tenantID: tenantIdOverride,
                        },
                      }),
                      transform: (roleList): LookupInputOption[] =>
                        lookups({ ...roleList, label: (r) => r.displayName() }),
                    }}
                    formValueMatcher={ReferenceValueMatcher}
                    onMatchChange={(matches): void => {
                      formikBag.setFieldValue('roles', OptionsToReferences(matches));
                    }}
                  />
                  {uneditableRoles.map(({ id, name }) => (
                    <div>
                      <Tag key={id} variant='Secondary' label={name} />
                      <FormHelperText>&nbsp;</FormHelperText>
                    </div>
                  ))}
                </Box>

                <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                  <Box flex={1}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={formikBag.values.allMarkets}
                          onChange={(event) => {
                            if (event.target.checked) {
                              SetFormikValues(formikBag, {
                                allMarkets: event.target.checked,
                                marketSummaries: [],
                              });
                            } else {
                              SetFormikValue(formikBag, 'allMarkets', event.target.checked);
                            }
                          }}
                        />
                      }
                      label='All markets'
                    />
                    <FormHelperText error={hasStringValue(formikBag.errors.allMarkets)}>
                      {hasStringValue(formikBag.errors.allMarkets)
                        ? formikBag.errors.allMarkets
                        : ' '}
                    </FormHelperText>
                  </Box>
                  <Box flex={3}>
                    {!formikBag.values.allMarkets && (
                      <ApiMultiLookupInput
                        formState={formikBag.values}
                        label='Markets'
                        name='marketSummaries'
                        route={{
                          barrel: editAsAdmin
                            ? 'GET market summaries as admin'
                            : 'GET market summaries',
                          args: (input, { constructQueryParams }) => ({
                            ...constructQueryParams(input),
                            pathParams: {
                              tenantID: tenantIdOverride ?? '',
                            },
                          }),
                          transform: (marketList): LookupInputOption[] =>
                            lookups({
                              ...marketList,
                              label: (m) => m.name,
                            }),
                        }}
                        formValueMatcher={ReferenceValueMatcher}
                        onMatchChange={(matches): void => {
                          formikBag.setFieldValue('marketSummaries', OptionsToReferences(matches));
                        }}
                      />
                    )}
                  </Box>
                </Box>

                <Box display='flex' flexDirection='column' gap={2} mt={1}>
                  <Divider />

                  <Typography variant='h2'>Bulk Edit Permissions</Typography>

                  <Alert severity='info'>
                    Allow this user to perform bulk edit operations on quotes, projects, companies,
                    locations, and activities
                  </Alert>

                  <FormControlLabel
                    control={
                      <Switch
                        checked={formikBag.values.canBulkEdit}
                        onChange={(event) => {
                          const { checked } = event.target;
                          if (!checked) {
                            SetFormikValues(formikBag, {
                              canBulkEdit: false,
                              canBulkEditQuotes: false,
                              canBulkEditProjects: false,
                              canBulkEditCompanies: false,
                              canBulkEditLocations: false,
                              canBulkEditActivities: false,
                            });
                            return;
                          }

                          SetFormikValue(formikBag, 'canBulkEdit', true);
                        }}
                      />
                    }
                    label='Can Bulk Edit'
                  />

                  {formikBag.values.canBulkEdit && (
                    <Box
                      sx={{
                        px: 2,
                        py: 1,
                        backgroundColor: theme.palette.SlabGray[50],
                      }}
                    >
                      {BULK_EDIT_PERMISSIONS.map((permission) => {
                        const { label, subLabel } = bulkEditPermissionData[permission];
                        const field = BULK_EDIT_FIELD_MAP[permission];
                        return (
                          <FormControlLabel
                            key={field}
                            sx={{
                              display: 'flex',
                              justifyContent: 'space-between',
                              mx: 0,
                              py: 0.5,
                            }}
                            control={
                              <Switch
                                checked={formikBag.values[field]}
                                onChange={(event) => {
                                  const { checked } = event.target;
                                  SetFormikValue(formikBag, field, checked);

                                  if (checked) {
                                    if (!formikBag.values.canBulkEdit) {
                                      SetFormikValue(formikBag, 'canBulkEdit', true);
                                    }
                                    return;
                                  }

                                  const hasAnyEditablePermissionEnabled =
                                    BULK_EDIT_PERMISSIONS.some((p) =>
                                      BULK_EDIT_FIELD_MAP[p] === field
                                        ? checked
                                        : formikBag.values[BULK_EDIT_FIELD_MAP[p]],
                                    );

                                  if (
                                    !hasAnyEditablePermissionEnabled &&
                                    formikBag.values.canBulkEdit
                                  ) {
                                    SetFormikValue(formikBag, 'canBulkEdit', false);
                                  }
                                }}
                              />
                            }
                            label={
                              <Box
                                sx={{
                                  display: 'flex',
                                  gap: 0.5,
                                  alignItems: 'center',
                                }}
                              >
                                <Typography variant='body1'>{label}</Typography>
                                {hasStringValue(subLabel) && (
                                  <Typography
                                    variant='caption'
                                    sx={{ top: 1, position: 'relative' }}
                                    color={theme.palette.SlabGray[600]}
                                  >
                                    ({subLabel})
                                  </Typography>
                                )}
                              </Box>
                            }
                            labelPlacement='start'
                          />
                        );
                      })}
                    </Box>
                  )}
                </Box>

                <Box display='flex' flexDirection='column' gap={2} mt={1}>
                  <Divider />

                  <Typography variant='h2'>Sensitive Files</Typography>

                  <Alert severity='info'>
                    Allow this user to upload files marked sensitive and to view, download, and
                    delete sensitive files. Users without this permission never see them.
                  </Alert>

                  <FormControlLabel
                    control={
                      <Switch
                        checked={formikBag.values.canViewSensitiveFiles}
                        onChange={(event) =>
                          SetFormikValue(formikBag, 'canViewSensitiveFiles', event.target.checked)
                        }
                      />
                    }
                    label='Can access sensitive files'
                  />
                </Box>

                {restrictsActivityEditing && (
                  <Box display='flex' flexDirection='column' gap={2} mt={1}>
                    <Divider />

                    <Typography variant='h2'>Activities</Typography>

                    <Alert severity='info'>
                      This organization limits users to editing only the activities assigned to them
                      or created by them. Allow this user to edit activities assigned to anyone.
                    </Alert>

                    <FormControlLabel
                      control={
                        <Switch
                          checked={
                            roleGrantsEditAllActivities(formikBag.values.roles) ||
                            formikBag.values.canEditAllActivities
                          }
                          disabled={roleGrantsEditAllActivities(formikBag.values.roles)}
                          onChange={(event): void =>
                            SetFormikValue(formikBag, 'canEditAllActivities', event.target.checked)
                          }
                        />
                      }
                      label={
                        <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
                          <Typography variant='body1'>Can edit all activities</Typography>
                          {roleGrantsEditAllActivities(formikBag.values.roles) && (
                            <Typography
                              variant='caption'
                              sx={{ top: 1, position: 'relative' }}
                              color={theme.palette.SlabGray[600]}
                            >
                              (granted by role)
                            </Typography>
                          )}
                        </Box>
                      }
                    />
                  </Box>
                )}

                {ctx.userInfo.hasFlags([Enums.FeatureFlagName.FeatureFlagPlanningRelease]) && (
                  <Box display='flex' flexDirection='column' gap={2} mt={1}>
                    <Divider />

                    <Typography variant='h2'>Planning</Typography>

                    <Alert severity='info'>
                      View opens the Planning page, its reports, and exports. Edit allows changing
                      structure, budgets, forecasts, and reporting groups. Approve allows locking a
                      budget for a location and planning year.
                    </Alert>

                    {PLANNING_PERMISSIONS.map((permission) => {
                      const field = PLANNING_FIELD_MAP[permission];
                      const grantedByRole = roleGrantedPlanning(formikBag.values.roles).has(
                        permission,
                      );
                      return (
                        <FormControlLabel
                          key={field}
                          control={
                            <Switch
                              checked={grantedByRole || formikBag.values[field]}
                              disabled={grantedByRole}
                              onChange={(event) =>
                                SetFormikValues(
                                  formikBag,
                                  setPlanningPermission(field, event.target.checked),
                                )
                              }
                            />
                          }
                          label={
                            <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
                              <Typography variant='body1'>
                                {planningPermissionData[permission].label}
                              </Typography>
                              {grantedByRole && (
                                <Typography
                                  variant='caption'
                                  sx={{ top: 1, position: 'relative' }}
                                  color={theme.palette.SlabGray[600]}
                                >
                                  (granted by role)
                                </Typography>
                              )}
                            </Box>
                          }
                        />
                      );
                    })}
                  </Box>
                )}
              </Box>
            </Box>
            <Box sx={{ display: 'flex', gap: 2, '& button': { flex: 1 } }}>
              <SlabButton
                variant='slab-secondary'
                onClick={() => requestClose(formikBag)}
                disabled={createUser.isPending || updateUser.isPending}
              >
                Cancel
              </SlabButton>
              <SlabButton
                variant='slab-secondary'
                onClick={(): void => formikBag.resetForm({ values: formikUser })}
                disabled={!formikBag.dirty || createUser.isPending || updateUser.isPending}
              >
                Reset
              </SlabButton>
              <SlabButton
                variant='slab-primary'
                onClick={formikBag.submitForm}
                loading={createUser.isPending || updateUser.isPending}
                disabled={!formikBag.dirty || createUser.isPending || updateUser.isPending}
              >
                {isEditMode ? 'Update User' : 'Save'}
              </SlabButton>
            </Box>
          </SlabDrawerLayout>
        )}
      </Formik>
    </SlabDrawer>
  );
};
